<template>
  <b-card>
    <b-overlay
      :show="isLoading || isSaving"
      rounded="sm"
    >
      <h4 class="mb-1">
        {{ currentRt.round_name || 'Раунд' }}, Тема {{ currentRt.order || currentRt.position }}
      </h4>

      <b-form-group
        v-if="showFormatPicker"
        label="Тип вопросов темы"
        class="mb-1"
      >
        <v-select
          :value="questionFormat"
          :options="questionFormatOptions"
          :reduce="option => option.value"
          :clearable="false"
          label="label"
          style="max-width: 260px"
          @input="value => $emit('format-changed', value)"
        />
      </b-form-group>

      <div v-if="!isRandom">
        <div v-if="suggestedTopic">
          <h2 class="my-2">
            {{ suggestedTopic.title }}
          </h2>
          <p
            v-if="availableItems !== null"
            class="text-muted mb-2"
          >
            Доступно элементов: {{ availableItems }}
          </p>
          <div class="d-flex" style="gap: 8px">
            <b-button
              variant="success"
              :disabled="isSaving"
              @click="$emit('accepted', suggestedTopic.id)"
            >
              Принять тему
            </b-button>
            <b-button
              variant="outline-secondary"
              :disabled="isLoading"
              @click="$emit('skipped')"
            >
              Другая тема
            </b-button>
          </div>
        </div>

        <div v-else-if="!isLoading">
          <p class="text-muted my-2">
            Нет доступных тем
          </p>
        </div>

        <hr>

        <!-- Manual topic select -->
        <div>
          <label class="mb-50">Или выберите тему вручную</label>
          <v-select
            v-model="manualTopic"
            :options="topicOptions"
            :filterable="false"
            label="title"
            placeholder="Выберите тему..."
            @search="onSearch"
          >
            <template #option="option">
              {{ option.title }}
              <span class="text-muted ml-50">({{ option.count || 0 }})</span>
            </template>
            <template #selected-option="option">
              {{ option.title }}
              <span class="text-muted ml-50">({{ option.count || 0 }})</span>
            </template>
            <template #no-options>
              <span v-if="isSearching">Поиск...</span>
              <span v-else>Ничего не найдено</span>
            </template>
          </v-select>
          <b-button
            variant="success"
            class="mt-1"
            :disabled="!manualTopic || isSaving"
            @click="$emit('accepted', manualTopic.id)"
          >
            Принять выбранную тему
          </b-button>
        </div>
      </div>

      <div v-if="isRandom">
        <b-form-group
          label="Название темы"
          label-for="random-topic-name"
        >
          <b-form-input
            id="random-topic-name"
            v-model="randomTopicName"
            style="max-width: 320px"
          />
        </b-form-group>
        <b-button
          variant="success"
          :disabled="isSaving || !randomTopicName.trim()"
          @click="$emit('random-started', randomTopicName.trim())"
        >
          Начать тему
        </b-button>
      </div>
    </b-overlay>
  </b-card>
</template>

<script>
import { BCard, BButton, BOverlay, BFormGroup, BFormInput } from 'bootstrap-vue'
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import store from '@/store'
import { QUESTION_FORMAT_OPTIONS, isRandomFormat, formatTopicPrefix } from './gameBuilderMusicFormats'

export default {
  components: {
    BCard,
    BButton,
    BOverlay,
    BFormGroup,
    BFormInput,
    vSelect,
  },
  props: {
    suggestedTopic: {
      type: Object,
      default: null,
    },
    availableItems: {
      type: Number,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    currentRt: {
      type: Object,
      required: true,
    },
    gameType: {
      type: String,
      default: null,
    },
    showFormatPicker: {
      type: Boolean,
      default: false,
    },
    questionFormat: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const manualTopic = ref(null)
    const topicOptions = ref([])
    const defaultOptions = ref([])
    const isSearching = ref(false)
    let searchTimeout = null

    const randomTopicName = ref('')
    const isRandom = computed(() => isRandomFormat(props.questionFormat))

    watch(() => props.questionFormat, value => {
      randomTopicName.value = isRandomFormat(value) ? formatTopicPrefix(value) : ''
    }, { immediate: true })

    const autotopicType = () => {
      const map = {
        movies: 'movie',
        series: 'series',
        music: 'music',
        'builder-movies': 'movie',
        'builder-series': 'series',
        'builder-music': 'music',
      }
      return map[props.gameType] || props.gameType || undefined
    }

    onMounted(() => {
      store.dispatch('game-builder/fetchAutotopics', {
        sort_by: 'items_count',
        sort_dir: 'desc',
        per_page: 100,
        type: autotopicType(),
      })
        .then(response => {
          const data = response.data.data || response.data
          const list = Array.isArray(data) ? data : []
          defaultOptions.value = list
          topicOptions.value = list
        })
        .catch(() => {
          topicOptions.value = []
        })
    })

    const onSearch = (query, loading) => {
      if (searchTimeout) clearTimeout(searchTimeout)
      if (!query) {
        topicOptions.value = defaultOptions.value
        return
      }
      searchTimeout = setTimeout(() => {
        isSearching.value = true
        loading(true)
        store.dispatch('game-builder/fetchAutotopics', {
          q: query,
          per_page: 100,
          type: autotopicType(),
        })
          .then(response => {
            const data = response.data.data || response.data
            topicOptions.value = Array.isArray(data) ? data : []
          })
          .catch(() => {
            topicOptions.value = []
          })
          .finally(() => {
            isSearching.value = false
            loading(false)
          })
      }, 300)
    }

    return {
      manualTopic,
      topicOptions,
      isSearching,
      onSearch,
      questionFormatOptions: QUESTION_FORMAT_OPTIONS,
      randomTopicName,
      isRandom,
    }
  },
}
</script>
